/**
 * The Platform Analytics workspace contract, for individual platform pages.
 *
 * Platform Analytics is the canonical page COMPOSITION, not just a source of
 * reusable CSS: one glowing analytics workspace holding EDEN, the control row,
 * the basis strip, the KPI strip, delivery, and every deeper module. An
 * individual platform page is that same page with an explicit platform scope —
 * the platform changes the data, the hero identity, the available metrics and
 * the deeper modules, never the page's geometry.
 *
 * This module carries the parts of that composition that are logic rather than
 * layout: the control vocabulary and the window arithmetic behind it. It is
 * byte-identical across Cortex and Quantum so the two cannot drift into
 * offering different date ranges or resolving the same range differently.
 *
 * Everything here is pure — no fetch, no storage, no clock beyond a reference
 * date a caller passes in.
 */

import { addCalendarDays, reportingToday } from "../utils/reportingCalendar";

/*
 * Campaign flight first, because it is the governed default an individual
 * platform page already resolves from the approved plan. The presets match
 * Platform Analytics exactly; a page must not invent a range its sibling
 * surfaces do not offer.
 */
export const PLATFORM_WINDOW_PRESETS = Object.freeze([
  { key: "flight", label: "Campaign flight", days: null },
  { key: "last_7d", label: "Last 7 days", days: 7 },
  { key: "last_14d", label: "Last 14 days", days: 14 },
  { key: "last_30d", label: "Last 30 days", days: 30 },
  { key: "last_90d", label: "Last 90 days", days: 90 },
]);

export const PLATFORM_COMPARISON_MODES = Object.freeze([
  { key: "prior_period", label: "vs prior period" },
  { key: "none", label: "No comparison" },
]);

/*
 * Net media basis is server-gated (spendNetBasisVerified is false), so the
 * control renders in the same position with the same vocabulary as Platform
 * Analytics and is disabled with the governed reason rather than omitted.
 * Removing it would hide that a basis choice exists and is being withheld.
 */
export const PLATFORM_SPEND_BASES = Object.freeze([
  { key: "net", shortLabel: "Net", label: "Net media cost" },
  { key: "gross", shortLabel: "Gross", label: "Gross (client-billed)" },
]);

export const DEFAULT_PLATFORM_SPEND_BASIS = "gross";
export const DEFAULT_PLATFORM_WINDOW = "flight";

/*
 * FULL DELIVERY — everything the campaign actually delivered: start through
 * max(latest reported delivery, authorized delivery end, original flight
 * end). A reporting view, never an authorization claim. Deliberately NOT a
 * member of PLATFORM_WINDOW_PRESETS: it is offered only when it ends
 * strictly later than the campaign flight for the current scope, so the
 * control row stays uncluttered for the unamended majority.
 */
export const FULL_DELIVERY_WINDOW_KEY = "full_delivery";
export const FULL_DELIVERY_WINDOW_LABEL = "Full delivery";

export function resolvePlatformWindowPreset(key) {
  return (
    PLATFORM_WINDOW_PRESETS.find((preset) => preset.key === String(key || "")) ||
    PLATFORM_WINDOW_PRESETS[0]
  );
}

/**
 * Resolve the reporting window a platform page should request.
 *
 * `flight` returns the governed flight timeframe untouched — the page already
 * resolved it from the approved plan and this layer must not second-guess it.
 * A preset produces an explicit override, which is the contract
 * analyticsToplineService already understands (`isExplicitOverride`), so the
 * request stays on the canonical path rather than a parallel one.
 *
 * The prior window is always the immediately preceding window of equal length,
 * which is the only comparison the reporting contract can support without
 * inventing a baseline.
 */
export function buildPlatformWindow({
  rangeKey = DEFAULT_PLATFORM_WINDOW,
  flightTimeframe = null,
  fullDeliveryTimeframe = null,
  reference = new Date(),
} = {}) {
  /*
   * FULL DELIVERY resolves exactly like the flight: the page already
   * resolved the governed window and this layer must not second-guess it.
   * Without a resolved full-delivery timeframe the key falls through to the
   * default flight resolution rather than inventing a window.
   */
  if (rangeKey === FULL_DELIVERY_WINDOW_KEY && fullDeliveryTimeframe) {
    return {
      presetKey: FULL_DELIVERY_WINDOW_KEY,
      label: FULL_DELIVERY_WINDOW_LABEL,
      timeframe: fullDeliveryTimeframe,
      startDate: fullDeliveryTimeframe?.start || null,
      endDate: fullDeliveryTimeframe?.end || null,
      priorStartDate: null,
      priorEndDate: null,
      comparisonSupported: false,
      comparisonUnsupportedReason:
        "The full delivery window has no comparable preceding period. Choose a fixed range to compare.",
    };
  }

  const preset = resolvePlatformWindowPreset(rangeKey);

  if (preset.key === "flight" || !preset.days) {
    const start = flightTimeframe?.start || null;
    const end = flightTimeframe?.end || null;
    return {
      presetKey: "flight",
      label: preset.label,
      timeframe: flightTimeframe,
      startDate: start,
      endDate: end,
      priorStartDate: null,
      priorEndDate: null,
      /*
       * A flight has no equal-length predecessor that means anything — the
       * period before a campaign started is not a comparable window — so the
       * comparison is declared unsupported rather than computed.
       */
      comparisonSupported: false,
      comparisonUnsupportedReason:
        "A campaign flight has no comparable preceding period. Choose a fixed range to compare.",
    };
  }

  /*
   * Calendar-day windows ending on the Eastern reporting day (they ended on
   * the UTC date — already "tomorrow" from 8pm Eastern). Each keeps its
   * inclusive span: the end and the (days - 1) calendar days before it, and
   * the prior window is the equal-length span immediately before.
   */
  const endDate = reportingToday(reference);
  const startDate = addCalendarDays(endDate, -(preset.days - 1));
  const priorEndDate = addCalendarDays(startDate, -1);
  const priorStartDate = addCalendarDays(priorEndDate, -(preset.days - 1));

  return {
    presetKey: preset.key,
    label: preset.label,
    timeframe: {
      start: startDate,
      end: endDate,
      datePreset: preset.key,
      source: "explicit_user",
      isExplicitOverride: true,
    },
    startDate,
    endDate,
    priorStartDate,
    priorEndDate,
    comparisonSupported: true,
    comparisonUnsupportedReason: null,
  };
}

/**
 * The prior-window timeframe for a resolved window, or null when the window
 * cannot support a comparison.
 */
export function buildPlatformPriorWindow(resolvedWindow) {
  if (!resolvedWindow?.comparisonSupported) return null;
  if (!resolvedWindow.priorStartDate || !resolvedWindow.priorEndDate) return null;
  return {
    start: resolvedWindow.priorStartDate,
    end: resolvedWindow.priorEndDate,
    datePreset: resolvedWindow.presetKey,
    source: "explicit_user",
    isExplicitOverride: true,
  };
}

/*
 * Campaign options for the scope control. Only campaigns that actually carry
 * this platform are offered, so selecting one can never scope the page to a
 * campaign that never ran there.
 */
/*
 * A parent platform covers the platforms it is the parent of. Campaigns are
 * tagged by the publisher they actually ran on — "facebook", "instagram" —
 * never by the parent, so matching the parent key alone would hide every real
 * Meta campaign from the Meta page's campaign scope while the control still
 * looked healthy.
 */
const PLATFORM_FAMILY_MEMBERS = {
  meta: ["meta", "facebook", "instagram", "audience_network", "messenger"],
};

export function buildPlatformCampaignOptions(campaigns = [], platformKey = "") {
  const normalizedPlatform = String(platformKey || "").trim().toLowerCase();
  const acceptedPlatforms =
    PLATFORM_FAMILY_MEMBERS[normalizedPlatform] || [normalizedPlatform];

  const carriesPlatform = (campaign) => {
    if (!normalizedPlatform) return true;
    const sources = [
      campaign?.platforms,
      campaign?.target_platforms,
      campaign?.targetPlatforms,
      campaign?.strategy_payload?.platforms,
    ];
    const values = [];
    sources.forEach((value) => {
      if (Array.isArray(value)) values.push(...value);
      else if (typeof value === "string" && value.trim()) values.push(...value.split(","));
    });
    if (!values.length) return true;
    return values
      .map((entry) => String(entry || "").trim().toLowerCase().replace(/[\s-]+/g, "_"))
      .some(
        (entry) =>
          acceptedPlatforms.includes(entry) ||
          (entry === "connected_tv" && normalizedPlatform === "ctv")
      );
  };

  return (Array.isArray(campaigns) ? campaigns : [])
    .filter(carriesPlatform)
    .map((campaign) => ({
      id: campaign?.id ?? campaign?.campaign_id ?? null,
      name:
        String(campaign?.campaign_name || campaign?.campaignName || campaign?.name || "").trim() ||
        null,
      flightStartDate:
        campaign?.flight_start_date || campaign?.flightStartDate || campaign?.flightStart || null,
      flightEndDate:
        campaign?.flight_end_date || campaign?.flightEndDate || campaign?.flightEnd || null,
      // The amendment/lifecycle contracts ride along so the scope window
      // resolvers and provenance helpers can read them off the option rows.
      authorizedDeliveryEndDate:
        campaign?.authorized_delivery_end_date ||
        campaign?.authorizedDeliveryEndDate ||
        null,
      deliveryExtended:
        campaign?.delivery_extended === true || campaign?.deliveryExtended === true,
      fullDeliveryWindow:
        campaign?.fullDeliveryWindow || campaign?.full_delivery_window || null,
      reportingContract: campaign?.reportingContract || campaign?.reporting_contract || null,
    }))
    .filter((option) => option.id != null && option.name)
    .filter((option, index, list) => list.findIndex((entry) => String(entry.id) === String(option.id)) === index);
}

/**
 * Percentage change against the prior window, or null when either side is
 * absent or the baseline is zero. A delta computed against a missing or zero
 * baseline is not a smaller number — it has no meaning.
 */
export function computePlatformDelta(current, prior) {
  const currentValue = typeof current === "number" && Number.isFinite(current) ? current : null;
  const priorValue = typeof prior === "number" && Number.isFinite(prior) ? prior : null;
  if (currentValue == null || priorValue == null || priorValue === 0) return null;
  return ((currentValue - priorValue) / priorValue) * 100;
}

export function formatPlatformDelta(delta) {
  if (typeof delta !== "number" || !Number.isFinite(delta)) return null;
  const rounded = Math.round(delta * 10) / 10;
  if (rounded === 0) return "0% vs prior";
  return `${rounded > 0 ? "↑ +" : "↓ "}${Math.abs(rounded) === rounded ? rounded : rounded}% vs prior`;
}
